// src/features/operacion/components/OperacionCard.tsx
//
// One of the Operación page's six director cards (Llegadas · Autoservicio
// completado · Resueltas sin recepción · Tiempo de entrega de la llave ·
// Llegadas con pre-check-in · Incidencias) — `KpiHero`'s layout with one addition: the coverage/
// freshness stamp under the number (CF S6), since KpiHero itself has no
// slot for it and this is the only page that needs one.
import React from 'react';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import { DeltaChip, type GoodDirection } from '@/components/common/DeltaChip';
import { WidgetCard } from '@/components/common/WidgetCard';
import { MetricStamp } from '@/features/insight-v2-shared/components/MetricStamp';
import { formatCount, DASH, type FormattedDelta } from '@/lib/analyticsFormat';
import { formatV2Duration, formatV2Share, formatV2CountDelta, formatV2ShareDelta, formatV2DurationDelta } from '@/lib/analyticsV2Format';
import { metricLabel } from '@/lib/v2MetricView';
import { useTranslate } from 'react-admin';
import type { OperacionCardDto } from '@/types/analyticsV2';

export type OperacionCardKind = 'count' | 'share' | 'duration';

interface OperacionCardProps {
  card?: OperacionCardDto;
  kind: OperacionCardKind;
  icon?: React.ReactNode;
  loading?: boolean;
  /** Which way is better for this figure (colours the change chip). */
  goodDirection?: GoodDirection;
}

const formatValue = (kind: OperacionCardKind, value: unknown): string => {
  if (kind === 'share') return formatV2Share(value);
  if (kind === 'duration') return formatV2Duration(value);
  return typeof value === 'number' ? formatCount(value) : DASH;
};

/** A card's change: points for a share, time for a duration (the API states it in seconds), units for a count. */
export const formatCardDelta = (kind: OperacionCardKind, delta: number): FormattedDelta => {
  if (kind === 'duration') return formatV2DurationDelta(delta);
  return kind === 'share' ? formatV2ShareDelta(delta) : formatV2CountDelta(delta);
};

export const OperacionCard: React.FC<OperacionCardProps> = ({ card, kind, icon, loading, goodDirection }) => {
  const theme = useTheme();
  const translate = useTranslate();
  const tint = theme.palette.dataAccent.dark;
  const metric = card?.current;
  const showDelta = card?.delta !== null && card?.delta !== undefined && card?.marker && card.marker !== 'sin_cambio';

  return (
    <WidgetCard title={metric ? metricLabel(metric, translate) : ''} icon={icon} accent={tint}>
      {loading ? (
        <Box sx={{ py: 1 }}>
          <Skeleton variant="text" width="55%" height={56} />
          <Skeleton variant="text" width="80%" />
        </Box>
      ) : !metric || metric.absent ? (
        <Typography sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 700, color: 'text.secondary', lineHeight: 1.3, py: 1 }}>
          {DASH}
        </Typography>
      ) : (
        <Typography sx={{ fontSize: { xs: 25, sm: 29 }, fontWeight: 800, letterSpacing: '-0.6px', lineHeight: 1.05, color: 'text.primary' }}>
          {formatValue(kind, metric.value)}
        </Typography>
      )}

      {!loading && metric && !metric.absent && showDelta && card && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          <DeltaChip delta={formatCardDelta(kind, card.delta as number)} goodDirection={goodDirection} />
        </Box>
      )}

      {!loading && metric && <MetricStamp stamp={metric.stamp} />}
    </WidgetCard>
  );
};

export default OperacionCard;
